import React, { useEffect } from "react";
import {
  UpsertInvoiceModel,
  InvoiceGridModel,
  InvoiceDetailPageModel,
  RelatedPaymentsGridModel,
} from "../../../shared/models";
import {
  withRouter,
  CustomLabel,
  FlexContainer,
  TableHeader,
  UtilityMethods,
  ActiveInactiveButton,
  ITableHeaders,
  ToastService,
} from "@mindmemobile/mm-shared-global";
import { NavigateFunction } from "react-router-dom";
import { observer } from "mobx-react";
import { debounce } from "lodash";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import DeleteInvoicePopUp from "./DeleteInvoicePopUp";
import EditIcon from "../../../../../../images/products/edit-blue.svg";
import PayIcon from "../../../../../../images/placeholders/pay-blue.svg";
import {
  AccountSettingsStore,
  InvoiceStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { AuthService, InvoiceService } from "../../../shared/services";
import { InvoiceUtil } from "../../../shared/utils";
import {
  GridNames,
  InvoiceStatus,
  ItemDropDownOptions,
} from "../../../shared/enums";
import {
  DashboardTopBar,
  DashboardTopRightSide,
  ItemContentArea,
  TableGrid,
  UpsertInvoicePopUp,
} from "../../../shared/components";
import styled from "styled-components";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";
import { debounceTime, Subscription } from "rxjs";

type IProps = {
  navigate: NavigateFunction;
  invoiceStore: InvoiceStore;
  itemStore: ItemsStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
  merchantAccountStore: MerchantAccountStore;
};

const InvoiceGridWrapper = styled.span`
  height: inherit;
  .invoice-edit-icon {
    cursor: pointer;
    height: 16px;
    width: 16px;
  }
  .invoice-delete-icon {
    cursor: pointer;
    height: 16px;
    width: 16px;
  }
  .invoice-pay-icon {
    cursor: pointer;
    height: 18px;
    width: 18px;
  }
`;
const InvoiceGrid: React.FC<IProps> = ({
  invoiceStore,
  navigate,
  itemStore,
  rolesAndPermissionStore,
  merchantAccountStore,
}) => {
  const [showLoader, setShowLoader] = React.useState(true);
  const [deleteDetails, setDeleteDetails] =
    React.useState<InvoiceGridModel | null>(null);
  const [invoicePayDetails, setInvoicePayDetails] =
    React.useState<UpsertInvoiceModel | null>(null);
  const [editInvoiceDetails, setEditInvoiceDetails] =
    React.useState<UpsertInvoiceModel | null>(null);
  const [firstTimeLoad, setFirstTimeLoad] = React.useState(true);

  useEffect(() => {
    itemStore.updateSearchValue("", ItemDropDownOptions.Invoices);
    loadData(
      invoiceStore.pagination.pageNumber,
      invoiceStore.pagination.pageSize,
      invoiceStore.searchValue,
      true
    );

    const subscription: Subscription = invoiceStore.searchSubject
      .pipe(debounceTime(500))
      .subscribe(() => {
        loadData(
          invoiceStore.pagination.pageNumber,
          invoiceStore.pagination.pageSize,
          invoiceStore.searchValue
        );
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  function loadData(
    pageNumber: number,
    pageSize: number,
    searchValue: string,
    firstTimeLoad: boolean = false
  ) {
    setFirstTimeLoad(firstTimeLoad);
    setShowLoader(true);
    InvoiceService.getInvoices(
      AuthService.authData.accountId,
      pageNumber,
      pageSize,
      searchValue
    ).subscribe({
      next: (response) => {
        setShowLoader(false);
        invoiceStore.deserializeGridData(response.result || [], response.meta);
      },
    });
  }

  function onPaginationChange(pageNumber: number, pageSize: number) {
    loadData(pageNumber, pageSize, invoiceStore.searchValue);
  }

  function goToDetailPage(id: string) {
    navigate(`/app/invoices/detail/${id}`);
  }

  function columns() {
    const columns: ITableHeaders<InvoiceGridModel>[] = [
      {
        dataKey: "status",
        title: "Status",
        onCellRender: (data: InvoiceGridModel) => {
          const colors = InvoiceUtil.getColorsByStatus(data.status);

          return (
            <ActiveInactiveButton bgColor={colors.bgColor} color={colors.color}>
              {InvoiceStatus[data.status]}
            </ActiveInactiveButton>
          );
        },
      },
      {
        dataKey: "dueDate",
        title: "Due",
        onCellRender: (data: InvoiceGridModel) => {
          return UtilityMethods.formatDateInStringWithTime(data.dueDate);
        },
      },
      {
        title: "Invoice No",
        dataKey: "invoiceNumber",
        onCellRender: (data: InvoiceGridModel) => {
          return data.invoiceNumber;
        },
      },
      {
        dataKey: "contact",
        title: "Contact",
        onCellRender: (data: InvoiceGridModel) => {
          return data.contact?.displayValue;
        },
      },
      {
        title: "Amount",
        dataKey: "amount",
        onCellRender: (data: InvoiceGridModel) => {
          return `$${AccountSettingsStore.formatAmount(data.amount)}`;
        },
      },
      {
        title: "Paid",
        dataKey: "paidAmount",
        onCellRender: (data: InvoiceGridModel) => {
          return `$${AccountSettingsStore.formatAmount(data.paidAmount)}`;
        },
      },
      {
        title: "Balance",
        dataKey: "balance",
        onCellRender: (data: InvoiceGridModel) => {
          return `$${AccountSettingsStore.formatAmount(data.balance)}`;
        },
      },
      {
        title: "Past Due",
        dataKey: "pastDueDays",
        onCellRender: (data: InvoiceGridModel) => {
          return (
            InvoiceStatus[data.status] === InvoiceStatus.PastDue && (
              <CustomLabel color="#FF0000">
                {data.pastDueDays} day(s)
              </CustomLabel>
            )
          );
        },
      },
      {
        dataKey: "pastDueDays",
        title: "Actions",
        onCellClick: (e) => {
          e.stopPropagation();
        },
        onCellRender: (data: InvoiceGridModel) => {
          return (
            <FlexContainer
              alignItems="center"
              justifyContent="flex-start"
              spacing={10}
            >
              {data.status !== "Paid" && (
                <img
                  src={EditIcon}
                  alt="Edit"
                  onClick={() => onEditOrPayClick(data.id, false)}
                  className="invoice-edit-icon"
                />
              )}

              {data.status === "Draft" && (
                <img
                  src={DeleteIcon}
                  alt="Delete"
                  onClick={() => onDeleteClick(data)}
                  className="invoice-delete-icon"
                />
              )}
              {data.isPayPopUpAllowed && (
                <img
                  src={PayIcon}
                  onClick={() => onEditOrPayClick(data.id, true)}
                  className="invoice-pay-icon"
                />
              )}
            </FlexContainer>
          );
        },
      },
    ];
    return columns;
  }

  function onDeleteClick(data: InvoiceGridModel) {
    if (!rolesAndPermissionStore.hasManagePermission("Delete")) {
      return;
    }
    setDeleteDetails(data);
  }

  function onDelete(refresh: boolean) {
    if (refresh) {
      loadData(
        invoiceStore.pagination.pageNumber,
        invoiceStore.pagination.pageSize,
        invoiceStore.searchValue
      );
    }
    setDeleteDetails(null);
  }

  function onEditOrPayClick(id: string, isPay: boolean) {
    if (!rolesAndPermissionStore.hasManagePermission(isPay ? "Pay" : "Edit")) {
      return;
    }
    InvoiceService.getInvoice(
      AuthService.authData.accountId,
      id,
      invoiceStore.searchValue
    ).subscribe({
      next: (response) => {
        if (response.result) {
          const details = InvoiceDetailPageModel.deserializeAPIModel(
            response.result
          );
          const payments = (response.result.payments || []).map((payment) =>
            RelatedPaymentsGridModel.deserializeAPIModel(payment)
          );
          const invoiceData = UpsertInvoiceModel.createModelFromInvoiceDetail(
            details.detail,
            payments,
            isPay
          );
          isPay
            ? setInvoicePayDetails(invoiceData)
            : setEditInvoiceDetails(invoiceData);
        }
      },
      error: () => {
        ToastService.showError("Error in getting invoice");
      },
    });
  }

  function closeEditOrPayPopUp(refresh: boolean) {
    setInvoicePayDetails(null);
    setEditInvoiceDetails(null);
    if (refresh) {
      loadData(
        invoiceStore.pagination.pageNumber,
        invoiceStore.pagination.pageSize,
        invoiceStore.searchValue
      );
    }
  }

  const isNoData =
    firstTimeLoad &&
    invoiceStore.pagination.totalItemCount === 0 &&
    !showLoader &&
    invoiceStore.searchValue === "";

  function renderDetails(node: React.ReactNode, showItemRightContent = true) {
    const content = showItemRightContent ? (
      <DashboardTopRightSide
        itemStore={itemStore}
        showSearchInput={!isNoData}
        showButton={!isNoData}
      />
    ) : (
      <></>
    );
    return (
      <>
        <DashboardTopBar
          itemStore={itemStore}
          rightContent={<>{showLoader && firstTimeLoad ? null : content}</>}
          merchantAccountStore={merchantAccountStore}
        />
        <ItemContentArea
          itemStore={itemStore}
          node={node}
          showLoader={showLoader}
          // className={isNoData ? "content-area-only" : ""}
          showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }

  return renderDetails(
    <InvoiceGridWrapper className="invoice-grid-wrapper">
      {invoicePayDetails && (
        <UpsertInvoicePopUp
          invoiceData={invoicePayDetails}
          onClose={(refresh: boolean) => closeEditOrPayPopUp(refresh)}
        />
      )}
      {editInvoiceDetails && (
        <UpsertInvoicePopUp
          invoiceData={editInvoiceDetails}
          onClose={(refresh: boolean) => closeEditOrPayPopUp(refresh)}
        />
      )}
      {deleteDetails && (
        <DeleteInvoicePopUp
          id={deleteDetails.id}
          balance={deleteDetails.balance}
          contact={deleteDetails.contact}
          dueDate={deleteDetails.dueDate}
          invoiceNumber={deleteDetails.invoiceNumber}
          status={deleteDetails.status}
          onClose={(deleted) => onDelete(deleted)}
        />
      )}
      <TableGrid
        height={
          invoiceStore.showFilter
            ? "calc(100vh - 499px)"
            : merchantAccountStore.hasNotificationBar()
              ? "calc(100vh - 350px)"
              : ""
        }
        tableHeader={
          <TableHeader
            showFilter={invoiceStore.showFilter}
            onToggleFilter={() => invoiceStore.toggleFilter()}
            total={invoiceStore.pagination.totalItemCount}
          />
        }
        totalItemCount={invoiceStore.pagination.totalItemCount}
        onRowClick={(data: InvoiceGridModel) => goToDetailPage(data.id)}
        cellSpacing={20}
        onPaginationChange={(pageNumber: number, pageSize: number) =>
          onPaginationChange(pageNumber, pageSize)
        }
        pagination={invoiceStore.pagination}
        data={invoiceStore.gridData}
        headers={columns()}
        name={GridNames.Invoice}
        hideTable={isNoData}
      />
    </InvoiceGridWrapper>
  );
};

export default withRouter(observer(InvoiceGrid));
